import { useEffect } from "react";
import { DEFAULT_OG_IMAGE } from "@/seo/site";

type MetaInput = {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  imageAlt?: string;
  /** Absolute canonical/og:url for this page. Use `absoluteUrl("/path")` so prerender bakes the prod origin. */
  canonicalUrl?: string;
  /** When true, emit `<meta name="robots" content="noindex,nofollow">` (transactional/private pages). */
  noindex?: boolean;
  /** One or more schema.org JSON-LD objects rendered into <script type="application/ld+json">. */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
};

function upsertMeta(selector: string, attrName: "name" | "property", attrValue: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Replace page-managed JSON-LD blocks. Tagged with data-seo-jsonld so we never touch the static shell's JSON-LD. */
function setJsonLd(blocks: Array<Record<string, unknown>>) {
  document.head.querySelectorAll("script[data-seo-jsonld]").forEach((n) => n.remove());
  for (const block of blocks) {
    const script = document.createElement("script");
    script.setAttribute("type", "application/ld+json");
    script.setAttribute("data-seo-jsonld", "");
    script.textContent = JSON.stringify(block);
    document.head.appendChild(script);
  }
}

/**
 * Imperatively sets document title and common meta/OpenGraph/Twitter tags, robots
 * directive, and JSON-LD for a page. Restores the previous title on unmount; other
 * tags are overwritten on the next page's call (and captured as-is by the prerenderer).
 */
export function useDocumentMeta({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage,
  imageAlt,
  canonicalUrl,
  noindex,
  jsonLd,
}: MetaInput) {
  // Stabilize the JSON-LD dependency so an inline array/object literal doesn't
  // re-run the effect every render.
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const image = ogImage ?? DEFAULT_OG_IMAGE;

    upsertMeta("meta[name='description']", "name", "description", description);
    upsertMeta("meta[name='robots']", "name", "robots", noindex ? "noindex,nofollow" : "index,follow");

    upsertMeta("meta[property='og:title']", "property", "og:title", ogTitle ?? title);
    upsertMeta("meta[property='og:description']", "property", "og:description", ogDescription ?? description);
    upsertMeta("meta[property='og:type']", "property", "og:type", "website");
    upsertMeta("meta[property='og:image']", "property", "og:image", image);
    if (canonicalUrl) {
      upsertMeta("meta[property='og:url']", "property", "og:url", canonicalUrl);
    }

    upsertMeta("meta[name='twitter:card']", "name", "twitter:card", "summary_large_image");
    upsertMeta("meta[name='twitter:title']", "name", "twitter:title", ogTitle ?? title);
    upsertMeta("meta[name='twitter:description']", "name", "twitter:description", ogDescription ?? description);
    upsertMeta("meta[name='twitter:image']", "name", "twitter:image", image);

    if (imageAlt) {
      upsertMeta("meta[property='og:image:alt']", "property", "og:image:alt", imageAlt);
      upsertMeta("meta[name='twitter:image:alt']", "name", "twitter:image:alt", imageAlt);
    }

    if (canonicalUrl) {
      upsertCanonical(canonicalUrl);
    }

    if (jsonLd) {
      setJsonLd(Array.isArray(jsonLd) ? jsonLd : [jsonLd]);
    }

    return () => {
      document.title = previousTitle;
    };
    // jsonLdKey captures jsonLd content; image/alt covered by their primitives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, ogTitle, ogDescription, ogImage, imageAlt, canonicalUrl, noindex, jsonLdKey]);
}
